import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { Hash, Radio, X } from "lucide-react";

import { PageWrapper } from "../../../../components/layout/container/Container";
import VideoSkeletonCard from "../../../../components/cards/skeleton/VideoSkeletonCard";
import AmazonIVSPlayer from "../../../../components/streams/AmazonIVSPlayer";

import { FormatDateToLocaleDateString } from "../../../../utils/DateTime";

import { useCreatorStreams } from "../../creators/hooks/useCreatorStreams";

import "./StreamLive.css";

const formatScheduledDateTime = (value) => {
  if (!value) return null;

  const scheduledDate = new Date(value);

  if (Number.isNaN(scheduledDate.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Melbourne",
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(scheduledDate);
};

const StreamLive = ({
  streamSessionId: streamSessionIdProp,
  isOpen = true,
  onClose,
}) => {
  const params = useParams();
  const navigate = useNavigate();

  const streamSessionId =
    streamSessionIdProp || params.streamSessionId || params.id;

  const {
    watchData,

    loading,
    error,

    getStreamWatchData,
    linkIvsRecordingToStream,

    resetStreamState,
  } = useCreatorStreams();

  const [linkingRecording, setLinkingRecording] = useState(false);

  const [linkRecordingMessage, setLinkRecordingMessage] = useState("");

  const handleClose = () => {
    if (typeof onClose === "function") {
      onClose();

      return;
    }

    navigate(-1);
  };

  useEffect(() => {
    if (!isOpen || !streamSessionId) {
      return;
    }

    setLinkRecordingMessage("");

    getStreamWatchData(streamSessionId);

    return () => {
      resetStreamState();
    };
  }, [isOpen, streamSessionId]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousTitle = document.title;

    document.title = `Katch | ${watchData?.title || "Live Stream"}`;

    return () => {
      document.title = previousTitle;
    };
  }, [isOpen, watchData?.title]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") {
        return;
      }

      if (typeof onClose === "function") {
        onClose();

        return;
      }

      navigate(-1);
    };

    document.addEventListener("keydown", handleKeyDown);

    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);

      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose, navigate]);

  const getErrorMessage = () => {
    if (!error) return "";

    if (typeof error === "string") {
      return error;
    }

    return (
      error?.response?.data?.message ||
      error?.message ||
      "Something went wrong while loading the stream."
    );
  };

  const getStatusLabel = (status) => {
    if (!status) return "Unknown";

    return String(status).replace(/_/g, " ").trim();
  };

  const handleLinkRecording = async () => {
    if (!streamSessionId) {
      return;
    }

    try {
      setLinkingRecording(true);
      setLinkRecordingMessage("");

      const result = await linkIvsRecordingToStream(streamSessionId);

      if (result === false || !result?.stream) {
        setLinkRecordingMessage(
          "Recording is not ready yet. Try again shortly.",
        );

        return;
      }

      await getStreamWatchData(streamSessionId);

      const replayReady = Boolean(
        result.stream?.vodHlsUrl ||
        result.stream?.vodMp4Url ||
        result.recording?.playbackUrl,
      );

      setLinkRecordingMessage(
        replayReady
          ? "Replay linked successfully."
          : "Recording linked. Replay is still processing.",
      );
    } catch (error) {
      console.error("Error linking stream recording:", error);

      setLinkRecordingMessage("Failed to link the recording replay.");
    } finally {
      setLinkingRecording(false);
    }
  };

  const renderCloseButton = () => (
    <button
      type="button"
      className="stream-live__close"
      onClick={handleClose}
      aria-label="Close live stream"
    >
      <X size={18} />
    </button>
  );

  const renderContent = () => {
    if (loading && !watchData?.streamSessionId) {
      return (
        <main className="stream-live stream-live--loading">
          <div className="stream-live__player-skeleton">
            {renderCloseButton()}
          </div>

          <VideoSkeletonCard />
        </main>
      );
    }

    if (error) {
      return (
        <main className="stream-live">
          <div className="stream-live__empty-shell">
            {renderCloseButton()}

            <div className="stream-live__empty">
              <div className="stream-live__empty-icon">
                <Radio size={26} />
              </div>

              <h1>Stream unavailable</h1>

              <p>{getErrorMessage()}</p>
            </div>
          </div>
        </main>
      );
    }

    if (!watchData?.streamSessionId) {
      return (
        <main className="stream-live">
          <div className="stream-live__empty-shell">
            {renderCloseButton()}

            <div className="stream-live__empty">
              <div className="stream-live__empty-icon">
                <Radio size={26} />
              </div>

              <h1>Stream not found</h1>

              <p>This live stream is no longer available.</p>
            </div>
          </div>
        </main>
      );
    }

    const status = String(watchData.status || "").toLowerCase();

    const isLive = status === "live";
    const isScheduled = status === "scheduled";
    const isEnded = status === "ended";

    const playbackUrl = watchData.ivsPlaybackUrl || watchData.ivs_playback_url;

    const hasLiveUrl = Boolean(playbackUrl);

    const hasReplay = Boolean(
      watchData.vodHlsUrl ||
      watchData.vod_hls_url ||
      watchData.vodMp4Url ||
      watchData.vod_mp4_url,
    );

    const canTryLinkRecording =
      !isLive &&
      !hasReplay &&
      ["ended", "recording_ready", "processing", "ready"].includes(status);

    const creatorName =
      watchData.brandName ||
      watchData.brand_name ||
      watchData.creatorName ||
      watchData.creator_name ||
      watchData.username ||
      "Katch Creator";

    const creatorInitial = creatorName.charAt(0).toUpperCase();

    const createdAt = watchData.createdAt || watchData.created_at;

    const formattedCreatedDate = createdAt
      ? FormatDateToLocaleDateString(createdAt)
      : null;

    const scheduledStart =
      watchData.scheduledStart ||
      watchData.scheduled_start ||
      watchData.scheduledAt ||
      watchData.scheduled_at;

    const scheduledDateTime = formatScheduledDateTime(scheduledStart);

    const displayDate =
      isScheduled && scheduledDateTime
        ? scheduledDateTime
        : formattedCreatedDate;

    const getPlaceholderTitle = () => {
      if (isScheduled) {
        return "Scheduled live stream";
      }

      if (isEnded) {
        return "The stream has ended";
      }

      if (status === "processing") {
        return "Replay processing";
      }

      if (status === "recording_ready") {
        return "Recording ready";
      }

      if (status === "ready") {
        return "Replay ready";
      }

      return "Stream is not live yet";
    };

    const getStreamMessage = () => {
      if (isScheduled) {
        if (scheduledDateTime) {
          return `This live stream is scheduled for ${scheduledDateTime}. It will appear here once broadcasting starts.`;
        }

        return "This live stream is scheduled, but the start date and time are unavailable.";
      }

      if (isEnded) {
        return "The stream has ended.";
      }

      if (status === "processing") {
        return "The stream has ended and the replay is being prepared.";
      }

      if (status === "recording_ready") {
        return "The recording is ready to be linked as a replay.";
      }

      if (status === "ready") {
        return "The stream has ended and the replay is ready to watch.";
      }

      return "The live stream will appear here once broadcasting starts.";
    };

    return (
      <main className="stream-live">
        <section>
          <div className="stream-live__player-shell">
            {renderCloseButton()}

            <div
              className={[
                "stream-live__status",
                `stream-live__status--${status}`,
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {isLive && <span className="stream-live__status-dot" />}

              {getStatusLabel(status)}
            </div>

            {isLive && hasLiveUrl && (
              <div className="stream-live__player-wrapper">
                <AmazonIVSPlayer
                  playbackUrl={playbackUrl}
                  autoPlay
                  muted={false}
                  controls
                />
              </div>
            )}

            {isLive && !hasLiveUrl && (
              <div className="stream-live__placeholder">
                <Radio size={28} />

                <h3>Live stream unavailable</h3>

                <p>The playback URL is missing from this stream.</p>
              </div>
            )}

            {!isLive && hasReplay && (
              <div className="stream-live__placeholder">
                <Radio size={28} />

                <h3>The stream has ended</h3>

                <p>
                  The replay is ready and can be watched from the replay
                  section.
                </p>
              </div>
            )}

            {!isLive && !hasReplay && (
              <div className="stream-live__placeholder">
                <Radio size={28} />

                <h3>{getPlaceholderTitle()}</h3>

                <p>{getStreamMessage()}</p>

                {isScheduled && scheduledDateTime && (
                  <div className="stream-live__scheduled-date">
                    <span>{scheduledDateTime}</span>
                  </div>
                )}

                {canTryLinkRecording && (
                  <button
                    type="button"
                    className="stream-live__link-button"
                    onClick={handleLinkRecording}
                    disabled={linkingRecording}
                  >
                    {linkingRecording
                      ? "Linking recording..."
                      : "Link recording replay"}
                  </button>
                )}

                {linkRecordingMessage && (
                  <p className="stream-live__link-message">
                    {linkRecordingMessage}
                  </p>
                )}
              </div>
            )}
          </div>
        </section>

        <section className="stream-live__info">
          <h1 className="stream-live__title">
            {watchData.title || "Untitled Stream"}
          </h1>

          <div className="stream-live__toolbar">
            <div className="stream-live__channel-info">
              <div className="stream-live__channel-avatar">
                {watchData.profileImageUrl || watchData.profile_image_url ? (
                  <img
                    src={
                      watchData.profileImageUrl || watchData.profile_image_url
                    }
                    alt={creatorName}
                  />
                ) : (
                  <span>{creatorInitial}</span>
                )}
              </div>

              <div className="stream-live__channel-meta">
                <span className="stream-live__channel-name">{creatorName}</span>

                {watchData.username && (
                  <span className="stream-live__channel-username">
                    {watchData.username}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="stream-live__description-card">
            {(displayDate || watchData.sport) && (
              <div className="stream-live__meta">
                {displayDate && (
                  <div className="stream-live__meta-item">
                    <span>
                      {isScheduled ? `Scheduled ${displayDate}` : displayDate}
                    </span>
                  </div>
                )}

                {watchData.sport && (
                  <div className="stream-live__sport-tag">
                    <Hash size={13} />

                    <span>{watchData.sport}</span>
                  </div>
                )}
              </div>
            )}

            <p className="stream-live__description-text">
              {watchData.description?.trim() ||
                "No description has been added for this stream."}
            </p>
          </div>
        </section>
      </main>
    );
  };

  if (!isOpen || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className="stream-live-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Live stream player"
    >
      <div className="stream-live-modal__overlay" onClick={handleClose} />

      <div className="stream-live-modal__content">
        <PageWrapper size="lg">{renderContent()}</PageWrapper>
      </div>
    </div>,
    document.body,
  );
};

export default StreamLive;
